import { FunctionComponent } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Stack,
  Button,
  Chip,
  Alert,
  LinearProgress,
} from "@mui/material";
import {
  FlagOutlined,
  CheckCircleOutline,
  WarningAmber,
  QuizOutlined,
  InfoOutlined,
} from "@mui/icons-material";
import { AlterDialogProps } from "./IExamSectionSubmit";
import "./SectionSubmitDialogTest.scss";

const SectionSubmitDialog: FunctionComponent<AlterDialogProps> = ({
  open,
  setOpen,
  messageheading,
  messageableName,
  questioncount,
  questioncountflag,
  listcount,
  listcountflag,
  rightButtons,
  leftButtons,
  
}) => {
  const unansweredList = Array.isArray(listcount) ? listcount : [];
  const flaggedList = Array.isArray(listcountflag) ? listcountflag : [];

  const unansweredCount =
    typeof questioncount === "number" ? questioncount : unansweredList.length;
  const flaggedCount =
    typeof questioncountflag === "number"
      ? questioncountflag
      : flaggedList.length;

  const totalQuestions = Math.max(
    ...[...unansweredList, ...flaggedList],
    unansweredCount + flaggedCount
  );
  const answeredQuestions = totalQuestions - unansweredCount;
  const completionPercentage =
    totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 100;
  const isComplete = completionPercentage === 100;

  return (
    <Dialog
      open={!!open}
      onClose={() => setOpen(false)}
      maxWidth="md"
      fullWidth
      classes={{ paper: "dialog-paper" }}
    >
      {/* Title Section */}
      <DialogTitle className="dialog-title">
        <Box className="title-icon">
          <QuizOutlined style={{ fontSize: 24 }} />
        </Box>
        <Box className="title-text">
          <Typography variant="h6" className="title-heading">
            {messageheading}
          </Typography>
          <Typography variant="body2" className="title-subheading">
            Review your progress before submitting
          </Typography>
        </Box>
      </DialogTitle>

      {/* Progress Bar */}
      <Box className="progress-container">
        <Box className="progress-text">
          <span>Progress</span>
          <span>{Math.round(completionPercentage)}% Complete</span>
        </Box>
        <LinearProgress
          variant="determinate"
          value={completionPercentage}
          className="progress-bar1"
          classes={{
            bar: `progress-bar1-filled ${
              isComplete ? "progress-complete" : "progress-incomplete"
            }`,
          }}
        />
      </Box>

      {/* Content Section */}
      <DialogContent className="dialog-content">
        {messageableName && (
          <Alert
            severity="info"
            className="alert-message"
            iconMapping={{
              info: <InfoOutlined className="alert-icon" />,
            }}
          >
            <Typography style={{ fontSize: "14px", lineHeight: 1.5 , fontWeight:"500"}}>
              {messageableName}
            </Typography>
          </Alert>
        )}

        <Stack spacing={2}>
          {/* Summary Cards */}
          <Box className="cards-container">
            {/* Unanswered Card */}
            <Box
              className={`summary-card ${
                unansweredCount > 0 ? "unanswered-card" : "answered-card"
              }`}
            >
              <Box className="card-content">
                {unansweredCount > 0 ? (
                  <WarningAmber className="card-icon unanswered-icon" />
                ) : (
                  <CheckCircleOutline className="card-icon success-icon" />
                )}
                <Box>
                  <Typography
                    variant="h5"
                    className={`card-text ${
                      unansweredCount > 0
                        ? "unanswered-text"
                        : "success-text"
                    }`}
                  >
                    {unansweredCount}
                  </Typography>
                  <Typography variant="body2" className="card-subtext">
                    Unanswered
                  </Typography>
                </Box>
              </Box>
              {unansweredCount === 0 && (
                <Typography
                  variant="body2"
                  style={{
                    color: "#16a34a",
                    fontWeight: 500,
                    fontSize: "0.8rem",
                    marginTop: 4,
                  }}
                >
                  All answered! 🎉
                </Typography>
              )}
            </Box>

            {/* Flagged Card */}
            <Box
              className={`summary-card ${
                flaggedCount > 0 ? "flagged-card" : "answered-card"
              }`}
            >
              <Box className="card-content">
                {flaggedCount > 0 ? (
                  <FlagOutlined className="card-icon flagged-icon" />
                ) : (
                  <CheckCircleOutline className="card-icon success-icon" />
                )}
                <Box>
                  <Typography
                    variant="h5"
                    className={`card-text ${
                      flaggedCount > 0 ? "flagged-text" : "success-text"
                    }`}
                  >
                    {flaggedCount}
                  </Typography>
                  <Typography variant="body2" className="card-subtext">
                    Flagged
                  </Typography>
                </Box>
              </Box>
              {flaggedCount === 0 && (
                <Typography
                  variant="body2"
                  style={{
                    color: "#16a34a",
                    fontWeight: 500,
                    fontSize: "0.8rem",
                    marginTop: 4,
                  }}
                >
                  No flags
                </Typography>
              )}
            </Box>
          </Box>

          {/* Unanswered Questions */}
          {unansweredCount > 0 && (
            <Box>
              <Typography
                variant="subtitle1"
                className={`section-title unanswered-title`}
              >
                <WarningAmber fontSize="small" />
                Unanswered ({unansweredCount})
              </Typography>
              <Box className="chips-container">
                {unansweredList.slice(0, 20).map((num) => (
                  <Chip
                    key={num}
                    label={`Question${num}`}
                    size="small"
                    className="question-chip unanswered-chip"
                  />
                ))}
                {unansweredList.length > 20 && (
                  <Chip
                    label={`+${unansweredList.length - 20}`}
                    size="small"
                    className="question-chip more-chip"
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Flagged Questions */}
          {flaggedCount > 0 && (
            <Box>
              <Typography
                variant="subtitle1"
                className={`section-title flagged-title`}
              >
                <FlagOutlined fontSize="small" />
                Flagged ({flaggedCount})
              </Typography>
              <Box className="chips-container">
                {flaggedList.slice(0, 20).map((num) => (
                  <Chip
                    key={num}
                    label={`Question${num}`}
                    size="small"
                    className="question-chip flagged-chip"
                  />
                ))}
                {flaggedList.length > 20 && (
                  <Chip
                    label={`+${flaggedList.length - 20}`}
                    size="small"
                    className="question-chip more-chip"
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Success Message */}
          {unansweredCount === 0 && flaggedCount === 0 && (
            <Alert
              severity="success"
              className="alert-message"
              iconMapping={{
                success: <CheckCircleOutline className="success-icon" />,
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                Great work! Ready to submit.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

{/* Actions */}
<DialogActions
  className="dialog-actions"
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
  }}
>
  <Box className="dialog-actions-left" sx={{ display: "flex", gap: 1 }}>
    {leftButtons}
  </Box>
  <Box className="dialog-actions-right" sx={{ display: "flex", gap: 1 }}>
    {rightButtons}
  </Box>
</DialogActions>

    </Dialog>
  );
};

export default SectionSubmitDialog;






// import { FunctionComponent } from "react";
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Typography,
//   Box,
//   Stack,
//   Button,
//   Divider,
// } from "@mui/material";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
// import { ReactComponent as Tick } from "../../Assets/Images/check-circle.svg";
// import { AlterDialogProps } from "./IExamSectionSubmit";

// const SectionSubmitDialog: FunctionComponent<AlterDialogProps> = ({
//   open,
//   setOpen,
//   messageheading,
//   messageableName,
//   questioncount,
//   questioncountflag,
//   listcount,
//   listcountflag,
//   buttonname,
// }) => {
//   const unansweredList = Array.isArray(listcount) ? listcount : [];
//   const flaggedList = Array.isArray(listcountflag) ? listcountflag : [];

//   const unansweredCount =
//     typeof questioncount === "number" ? questioncount : unansweredList.length;
//   const flaggedCount =
//     typeof questioncountflag === "number" ? questioncountflag : flaggedList.length;

//   return (
//     <Dialog
//       open={!!open}
//       onClose={() => setOpen(false)}
//       maxWidth="sm"
//       fullWidth
//       PaperProps={{
//         sx: { borderRadius: 3, overflow: "hidden" },
//       }}
//     >
//       {/* Title */}
//       <DialogTitle
//         sx={{
//           display: "flex",
//           alignItems: "center",
//           gap: 1.5,
//           bgcolor: "#f8fafc",
//           borderBottom: "1px solid #e2e8f0",
//         }}
//       >
//         <InfoOutlinedIcon sx={{ color: "#0ea5e9" }} />
//         <Typography fontWeight={700} fontSize="1.1rem">
//           {messageheading}
//         </Typography>
//       </DialogTitle>

//       {/* Content */}
//       <DialogContent sx={{ p: 3 }}>
//         {messageableName && (
//           <Typography
//             sx={{
//               color: "#334155",
//               fontSize: 15,
//               mb: 3,
//               lineHeight: 1.6,
//             }}
//           >
//             {messageableName}
//           </Typography>
//         )}

//         {/* Unanswered */}
//         {unansweredCount > 0 && (
//           <>
//             <Typography
//               variant="subtitle1"
//               fontWeight={600}
//               sx={{ color: "#dc2626", mb: 1 }}
//             >
//               Unanswered Questions ({unansweredCount})
//             </Typography>
//             <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
//               {unansweredList.map((num) => (
//                 <Button
//                   key={num}
//                   size="small"
//                   sx={{
//                     bgcolor: "#fee2e2",
//                     color: "#b91c1c",
//                     fontSize: "12px",
//                     textTransform: "none",
//                     borderRadius: 20,
//                     px: 1.5,
//                     "&:hover": { bgcolor: "#fecaca" },
//                   }}
//                 >
//                   Question {num}
//                 </Button>
//               ))}
//             </Box>
//             <Divider sx={{ mb: 2 }} />
//           </>
//         )}

//         {/* Flagged */}
//         <Typography
//           variant="subtitle1"
//           fontWeight={600}
//           sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
//         >
//           {flaggedCount > 0 ? (
//             <FlagOutlinedIcon sx={{ color: "#f59e0b" }} />
//           ) : (
//             <Tick />
//           )}
//           Flagged Questions ({flaggedCount})
//         </Typography>

//         {flaggedCount > 0 ? (
//           <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
//             {flaggedList.map((num) => (
//               <Button
//                 key={num}
//                 size="small"
//                 sx={{
//                   bgcolor: "#fef3c7",
//                   color: "#92400e",
//                   fontSize: "12px",
//                   textTransform: "none",
//                   borderRadius: 20,
//                   px: 1.5,
//                   "&:hover": { bgcolor: "#fde68a" },
//                 }}
//               >
//                 Question {num}
//               </Button>
//             ))}
//           </Box>
//         ) : (
//           <Typography sx={{ color: "#16a34a" }}>
//             No questions have been flagged.
//           </Typography>
//         )}
//       </DialogContent>

//       {/* Actions */}
//       <DialogActions
//         sx={{
//           bgcolor: "#f9fafb",
//           borderTop: "1px solid #f3f4f6",
//           p: 2,
//           display: "flex",
//           justifyContent: "space-between",
//         }}
//       >
//         <Button
//           onClick={() => setOpen(false)}
//           variant="outlined"
//           color="inherit"
//           sx={{ borderRadius: 2 }}
//         >
//           Return to Exam
//         </Button>
//         {buttonname}
//       </DialogActions>
//     </Dialog>
//   );
// };

// export default SectionSubmitDialog;
